import { ReactNode, useContext, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { List, ListItem, ListItemIcon, ListItemText, Stack, Typography, Divider, Button } from '@mui/material'
import { AddCircleOutline } from '@mui/icons-material'
import { com } from '@eidu/form'
import EntityTypeRepository from '../../io/EntityTypeRepository'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import usePermittedActions from '../../io/usePermittedActions'
import PermissionError from './PermissionError'
import ListItemButtonWithNavigation from '../../components/input/ListItemButtonWithNavigation'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import { iconForEntityTypeKind } from '../../components/misc/entityTypeIcons'
import { ADMIN_SCHEMA_NAME, getTypeDisplayName, parseTypeName } from '../../util/entityType/typeNameSchema'
import PermittedGlobalAction = com.eidu.sharedlib.entity.permission.PermittedGlobalAction
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import EntityType = com.eidu.sharedlib.entity.type.EntityType

type EntityTypesPageProps = { entityTypeRepository: EntityTypeRepository }

const EntityTypesPage = ({ entityTypeRepository }: EntityTypesPageProps) => {
  const navigate = useNavigate()

  const requested = useRef([new PermittedGlobalAction(GlobalAction.ManageEntityTypes)])
  const [permissions, permissionsError, permissionsLoading] = usePermittedActions(requested.current)

  const hasGlobalPermission = (action: GlobalAction) =>
    permissions?.some((permission) => permission.equals(new PermittedGlobalAction(action)))

  const entityTypes = entityTypeRepository.useSortedEntityTypes()

  type SectionItem = { id: EntityType['id']; name: string; icon: ReactNode }
  type Section = { title: string; items: SectionItem[]; dividerBefore?: boolean }

  const sections = useMemo(() => {
    if (!entityTypes) return [] as Section[]

    const items: SectionItem[] = entityTypes.map((type) => ({
      id: type.id,
      name: getTypeDisplayName(type.name),
      icon: iconForEntityTypeKind(type.kind),
    }))

    const schemaForType = (type: EntityType) => parseTypeName(type.name).schema ?? 'Other'
    const itemsBySchema = new Map<string, SectionItem[]>()

    entityTypes.forEach((type, index) => {
      const schemaName = schemaForType(type)
      const existing = itemsBySchema.get(schemaName) ?? []
      existing.push(items[index])
      itemsBySchema.set(schemaName, existing)
    })

    const sortByName = (a: SectionItem, b: SectionItem) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

    itemsBySchema.forEach((schemaItems) => schemaItems.sort(sortByName))

    const schemaNames = Array.from(itemsBySchema.keys())
    const normalizedAdmin = ADMIN_SCHEMA_NAME.toLowerCase()
    const sortedSchemas = [
      ...schemaNames.filter((name) => name.toLowerCase() === normalizedAdmin),
      ...schemaNames
        .filter((name) => name.toLowerCase() !== normalizedAdmin)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    ]

    return sortedSchemas.map((schemaName, index) => ({
      title: schemaName,
      items: itemsBySchema.get(schemaName) ?? [],
      dividerBefore: index !== 0,
    }))
  }, [entityTypes])

  if (permissionsLoading) return <LoadingOverlay isOpen />

  if (permissionsError) return <PermissionError missingPermittedActions={[GlobalAction.ManageEntityTypes]} />

  return (
    (hasGlobalPermission(GlobalAction.ManageEntityTypes) && (
      <Stack paddingX={3} paddingY={3} spacing={2.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" sx={{ mb: 1 }}>
            Types
          </Typography>
          <Button
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={() => navigate('new', { relative: 'path' })}
          >
            ADD
          </Button>
        </Stack>
        {sections.map(({ title, items, dividerBefore }) => (
          <Stack key={title} spacing={0.25}>
            <Typography variant="overline" sx={{ letterSpacing: 1.5, fontSize: '0.7rem', lineHeight: 1, pb: 0.25 }}>
              {title}
            </Typography>
            <Divider sx={{ mb: 0.5, mt: dividerBefore ? 1 : 0 }} />
            <List dense disablePadding>
              {items.map(({ name, id, icon }, index) => (
                <ListItem key={`entity-type-${id}`} disablePadding sx={{ py: index === 0 ? 0 : 0.25 }}>
                  <ListItemButtonWithNavigation path={`/types/${id.toString()}`}>
                    <ListItemIcon sx={{ minWidth: 30 }}>{icon}</ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: '0.9rem' }} primary={name} />
                  </ListItemButtonWithNavigation>
                </ListItem>
              ))}
            </List>
          </Stack>
        ))}
      </Stack>
    )) ||
    PermissionError({ missingPermittedActions: [GlobalAction.ManageEntityTypes] })
  )
}

const EntityTypesPageRoute = () => {
  const entityTypeRepository = useContext(EntityTypesContext)
  return <EntityTypesPage entityTypeRepository={entityTypeRepository} />
}

export default EntityTypesPageRoute
